/**
 * Modal de confirmación reusable — Sprint 17 S17-Z15.
 *
 * Reemplaza `window.confirm()` y `window.prompt()` con un modal
 * proper que respeta la identidad visual del portal. Soporta dos
 * modos:
 * - Confirmación simple (¿confirmar? sí/no).
 * - Confirmación con motivo obligatorio (anulaciones, retiros).
 *
 * Patrón: el caller monta `{open && <ModalConfirmar .../>}` igual
 * que el resto de modales del proyecto (ver Modal.tsx). Promesa-amigable:
 * `onConfirmar` puede ser async y el modal muestra "Procesando…"
 * mientras espera.
 */
import { useState } from 'react';
import { Button } from './Button';
import { Modal } from './Modal';

export interface ModalConfirmarProps {
  /** Título del modal (visible y aria-labelledby). */
  title: string;
  /** Mensaje explicativo arriba de los botones / textarea. */
  mensaje: string;
  /** Si true, pide motivo en textarea. Default: false (confirmación simple). */
  pedirMotivo?: boolean;
  /** Placeholder del textarea de motivo. */
  motivoPlaceholder?: string;
  /** Si true, el motivo no puede estar vacío. Default: true cuando
   *  pedirMotivo=true. Si pedirMotivo=false, este flag no aplica. */
  motivoRequerido?: boolean;
  /** Texto del botón confirmar. Default: "Confirmar". */
  textoConfirmar?: string;
  /** Variante visual del botón confirmar. Default: 'primary'. Para
   *  acciones destructivas usar 'danger'. */
  variantConfirmar?: 'primary' | 'danger';
  /** Callback al confirmar. Recibe el motivo (vacío si no pedirMotivo).
   *  Puede ser async; el modal muestra "Procesando…" mientras espera. */
  onConfirmar: (motivo: string) => void | Promise<void>;
  /** Callback al cancelar (ESC, click backdrop, botón Cancelar). */
  onCancelar: () => void;
  /** Error a mostrar dentro del modal (Sprint 17 Z21). El caller debe
   *  capturar el rechazo de `onConfirmar` y pasarlo aquí. El modal
   *  permanece abierto para que el usuario corrija. */
  error?: string | null;
}

export function ModalConfirmar({
  title,
  mensaje,
  pedirMotivo = false,
  motivoPlaceholder,
  motivoRequerido = true,
  textoConfirmar = 'Confirmar',
  variantConfirmar = 'primary',
  onConfirmar,
  onCancelar,
  error = null,
}: ModalConfirmarProps) {
  const [motivo, setMotivo] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const motivoValido =
    !pedirMotivo || !motivoRequerido || motivo.trim().length > 0;
  const puedeConfirmar = motivoValido && !submitting;

  async function handleConfirm() {
    if (!puedeConfirmar) return;
    setSubmitting(true);
    try {
      await onConfirmar(motivo.trim());
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={title}
      onClose={() => {
        if (!submitting) onCancelar();
      }}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onCancelar}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button
            variant={variantConfirmar}
            onClick={handleConfirm}
            disabled={!puedeConfirmar}
          >
            {submitting ? 'Procesando…' : textoConfirmar}
          </Button>
        </>
      }
    >
      <p className="text-sm text-text-secondary mb-3 whitespace-pre-line">
        {mensaje}
      </p>
      {pedirMotivo && (
        <label className="block">
          <span className="text-xs text-text-muted mb-1 block">
            Motivo {motivoRequerido && <span className="text-state-danger">*</span>}
          </span>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder={motivoPlaceholder}
            rows={3}
            disabled={submitting}
            className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-orange resize-none disabled:opacity-50"
            autoFocus
          />
        </label>
      )}
      {error && (
        <div
          className="mt-3 bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
          role="alert"
        >
          {error}
        </div>
      )}
    </Modal>
  );
}
